import { useState } from "react";
import useSWR from "swr";
import myaxios from "lib/myAxios";
import { requestTrial, trialStatus } from "lib/common/endpoints/endUser";
import { getOrCreateDeviceToken } from "utils/deviceToken";

export interface TrialStatus {
  status: "none" | "pending" | "active" | "denied" | "expired" | "converted";
  requestedAt?: number;
  startedAt?: number;
  endsAt?: number;
}

/**
 * Free trials are requested, not auto-granted -- a request just creates a
 * "pending" record for an admin to approve or deny (see
 * xylene/end_user/classroom/routes/routes.py TrialRequest/TrialStatus).
 * Decoupled from classroom registration: this can be called for any
 * classroom the user is logged in for, registered or not.
 */
function useTrialRequest(classroomId?: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, mutate } = useSWR<{ data: TrialStatus }>(
    classroomId ? trialStatus + classroomId : null,
  );

  const request = () => {
    if (!classroomId) return;
    setLoading(true);
    setError(null);

    myaxios
      .post(requestTrial + classroomId, { deviceId: getOrCreateDeviceToken() })
      .then(() => mutate())
      .catch((err) => {
        setError(err?.response?.data?.message ?? "Could not request a trial right now.");
      })
      .finally(() => setLoading(false));
  };

  return {
    trial: data?.data ?? { status: "none" as const },
    requestTrial: request,
    loading,
    error,
  };
}

export default useTrialRequest;
